import React, { useState } from 'react';

import { Skeleton, Typography } from '@mui/material';
import { MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTextarea } from 'src/components/AlineTextarea';
import { PanelHeader } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { useCheckWorkflowAuthorization, useGetWorkflowSimple } from 'src/hooks';
import { getIsWorkflowSigned, getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(3),
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sectionIcon: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
  },
  sectionLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

interface PhaseSettingsPanelProps {
  phaseId: UUID;
  workflowId: UUID;
}

export const PhaseSettingsPanel = React.memo(function PhaseSettingsPanel({
  phaseId: _phaseId,
  workflowId,
}: PhaseSettingsPanelProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const links = useWorkflowDetailLinks(workflowId);
  const { loading, workflow } = useGetWorkflowSimple(workflowId);
  const handleBack = () => navigate(links.baseUrl());

  if (loading || !workflow)
    return (
      <div className={classes.root}>
        <PanelHeader showBackButton text="Settings" onClickBack={handleBack} />
        <div className={classes.content}>
          <Skeleton height={120} variant="rectangular" width="100%" />
        </div>
      </div>
    );

  return (
    <div className={classes.root}>
      <PanelHeader showBackButton text="Settings" onClickBack={handleBack} />
      <div className={classes.content}>
        <SignatureMessageSection workflow={workflow} />
      </div>
    </div>
  );
});

type SettingsWorkflow = Pick<
  Apollo.Workflow,
  'envelopeMsg' | 'id' | '__typename' | 'state' | 'variant'
>;

function SignatureMessageSection({ workflow }: { workflow: SettingsWorkflow }) {
  const { classes } = useStyles();
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const [message, setMessage] = useState(workflow.envelopeMsg ?? '');
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflow.id);
  const isWorkflowSigned = getIsWorkflowSigned(workflow);
  const workflowSentForSig = getWorkflowWasSentForSig(workflow);
  const isDisabled = isWorkflowSigned || workflowSentForSig || !canEditWorkflow;

  const debouncedUpdate = useDebouncedCallback((text: string) => {
    void updateWorkflow({
      variables: { input: { props: { envelopeMsg: text }, workflowId: workflow.id } },
    });
  }, 500);

  const handleChange = (text: string) => {
    setMessage(text);
    debouncedUpdate(text);
  };

  return (
    <div>
      <div className={classes.sectionLabel}>
        <MessageSquare className={classes.sectionIcon} size={16} />
        <Typography className={classes.sectionTitle}>Signature Message</Typography>
      </div>
      {isWorkflowSigned ? (
        <Typography variant="body2">{workflow.envelopeMsg || 'No message.'}</Typography>
      ) : (
        <AlineTextarea
          disabled={isDisabled}
          maxRows={8}
          minRows={3}
          placeholder="Add a message.."
          value={message}
          onChange={e => handleChange(e.target.value)}
        />
      )}
    </div>
  );
}
